// A weapon at rest, on its own (no kid holding it): the same procedural mesh the slimes hold (character-weapons.js
// getWeaponDef), in the player's weapon skin and ink colour. Used by the inspect turntable (src/ui/inspect.js) and the
// shop / loadout tile portraits (showcase.js portrait kinds 'weapon' and 'sub').
import * as THREE from 'three';
import { WEAPONS, SUBS } from '../config.js';
import { getWeaponDef, getSubDef, makeCoilMaterial } from './character-weapons.js';
import { getPlasticMaterial, getInkMaterial } from './character-mats.js';
import { skinIndex } from './cosmetics.js';

/** → Group (dualies come as a pair), centred on its bounds; group.userData.dispose() frees its own materials. */
export function buildWeaponModel(id, color, skin) {
  const W = WEAPONS[id] || WEAPONS.shooter;
  const d = getWeaponDef(W.kind || id);
  const mats = [];
  const model = new THREE.Group();
  const add = (geo, mat, at) => { if (!geo) return; const m = new THREE.Mesh(geo, mat); if (at) m.position.copy(at); model.add(m); };
  const plastic = getPlasticMaterial(skinIndex(skin)), ink = getInkMaterial(color);
  add(d.body, plastic); add(d.ink, ink);
  if (d.drum) { add(d.drum, ink, d.drumAt); add(d.drumCaps, plastic, d.drumAt); }
  if (d.glow) { const coil = makeCoilMaterial(); coil.emissive.copy(color); mats.push(coil); add(d.glow, coil); }
  const out = new THREE.Group();
  if (d.dual || d.mirrorDual) {
    // pistols / dualies come in pairs: the second one beside the first
    const twin = model.clone(); twin.position.x -= 0.16; twin.position.y -= 0.05; twin.rotation.z = 0.08;
    out.add(model, twin);
  } else out.add(model);
  const c = new THREE.Box3().setFromObject(out).getCenter(new THREE.Vector3());
  for (const ch of out.children) ch.position.sub(c);
  out.userData.dispose = () => { for (const m of mats) m.dispose(); };
  return out;
}

/** A sub weapon on its own (config SUBS id: bomb, sticky, seeker…), centred on its bounds, in the team ink colour. */
export function buildSubModel(id, color) {
  const d = getSubDef((SUBS[id] && SUBS[id].kind) || id);
  const mats = [];
  const model = new THREE.Group();
  const add = (geo, mat) => { if (geo) model.add(new THREE.Mesh(geo, mat)); };
  add(d.body, getPlasticMaterial()); add(d.ink, getInkMaterial(color));
  if (d.glow) { const g = new THREE.MeshStandardMaterial({ color: color.clone().multiplyScalar(0.35), emissive: color.clone(), emissiveIntensity: 1.2, roughness: 0.35 }); mats.push(g); add(d.glow, g); }
  if (d.spin) {   // a spinning part (sprinkler head…): authored about its pivot
    const s = new THREE.Group(); if (d.spinAt) s.position.copy(d.spinAt); model.add(s);
    for (const [geo, mat] of [[d.spin, getPlasticMaterial()], [d.spinInk, getInkMaterial(color)]]) if (geo) { const m = new THREE.Mesh(geo, mat); if (d.spinAt) m.position.copy(d.spinAt).negate(); s.add(m); }
  }
  const out = new THREE.Group(); out.add(model);
  model.position.sub(new THREE.Box3().setFromObject(out).getCenter(new THREE.Vector3()));
  out.userData.dispose = () => { for (const m of mats) m.dispose(); };
  return out;
}
